import { computed, ref } from "vue";
import { useStore } from 'vuex';

const TRM_STORAGE_KEY = 'crm_trm_usd_cop';
const TRM_DEFAULT = 4100;

// Etiquetas legibles para los tres features que mide la API.
const FEATURE_LABELS = {
    chat: 'Chat',
    busqueda: 'Búsqueda',
    descripcion: 'Descripción',
};

const FEATURE_ICONS = {
    chat: 'fas fa-comments',
    busqueda: 'fas fa-search',
    descripcion: 'fas fa-align-left',
};

const FEATURE_COLORS = {
    chat: 'primary',
    busqueda: 'info',
    descripcion: 'warning',
};

export function useAiUsage() {
    const store = useStore();
    const spinner = ref(false);
    const alertTable = ref({ state: false, message: '', color: 'success' });

    // Por defecto: mes en curso (lo mismo que hace la API sin parámetros).
    const hoy = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const filtros = ref({
        desde: toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
        hasta: toISO(hoy),
        user_id: '',
    });

    const trm = ref(Number(localStorage.getItem(TRM_STORAGE_KEY)) || TRM_DEFAULT);

    const setTrm = (value) => {
        const num = Number(value);
        trm.value = num > 0 ? num : TRM_DEFAULT;
        localStorage.setItem(TRM_STORAGE_KEY, trm.value);
    };

    const getAiUsage = async () => {
        spinner.value = true;
        try {
            await store.dispatch('appAiUsage/getAiUsageAction', filtros.value);
        } catch (error) {
            const status = error.response?.status;
            if (status === 422) {
                showAlert('Las fechas seleccionadas no son válidas', 'danger');
            } else {
                showAlert('Error al cargar el consumo de IA', 'danger');
            }
        } finally {
            spinner.value = false;
        }
    };

    // Selector de usuario: búsqueda contra el listado de Membresías, con debounce
    let searchTimer = null;
    const buscarMiembros = (termino) => {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = setTimeout(() => {
            store.dispatch('appAiUsage/buscarMiembrosAction', termino);
        }, 350);
    };

    const aplicarFiltros = async () => {
        if (filtros.value.desde && filtros.value.hasta && filtros.value.desde > filtros.value.hasta) {
            showAlert('La fecha inicial no puede ser mayor que la final', 'warning');
            return;
        }
        await getAiUsage();
    };

    const limpiarFiltros = async () => {
        filtros.value = {
            desde: toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
            hasta: toISO(hoy),
            user_id: '',
        };
        await getAiUsage();
    };

    // Rangos rápidos
    const aplicarRango = async (rango) => {
        const ahora = new Date();
        if (rango === 'mes') {
            filtros.value.desde = toISO(new Date(ahora.getFullYear(), ahora.getMonth(), 1));
            filtros.value.hasta = toISO(ahora);
        } else if (rango === 'mesAnterior') {
            const inicio = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
            const fin = new Date(ahora.getFullYear(), ahora.getMonth(), 0);
            filtros.value.desde = toISO(inicio);
            filtros.value.hasta = toISO(fin);
        } else if (rango === '7dias') {
            const inicio = new Date(ahora);
            inicio.setDate(inicio.getDate() - 6);
            filtros.value.desde = toISO(inicio);
            filtros.value.hasta = toISO(ahora);
        } else if (rango === '30dias') {
            const inicio = new Date(ahora);
            inicio.setDate(inicio.getDate() - 29);
            filtros.value.desde = toISO(inicio);
            filtros.value.hasta = toISO(ahora);
        } else if (rango === 'anio') {
            filtros.value.desde = toISO(new Date(ahora.getFullYear(), 0, 1));
            filtros.value.hasta = toISO(ahora);
        }
        await getAiUsage();
    };

    const showAlert = (message, color = 'success') => {
        alertTable.value = { state: true, message, color };
        setTimeout(() => {
            alertTable.value.state = false;
        }, 5000);
    };

    // --- Formateadores ---------------------------------------------------
    // Las cifras son pequeñas: 4 decimales en USD, si no todo se ve como $0.00
    const formatUsd = (value) => {
        const num = Number(value || 0);
        return '$' + num.toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
        });
    };

    const formatCop = (usd) => {
        const num = Number(usd || 0) * trm.value;
        return '$' + Math.round(num).toLocaleString('es-CO') + ' COP';
    };

    const formatNumber = (value) => Number(value || 0).toLocaleString('es-CO');

    const formatMs = (value) => {
        const num = Number(value || 0);
        if (!num) return '—';
        if (num < 1000) return `${Math.round(num)} ms`;
        return `${(num / 1000).toFixed(1)} s`;
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '';
        const [y, m, d] = fecha.split('-');
        return `${d}/${m}/${y}`;
    };

    const featureLabel = (feature) => FEATURE_LABELS[feature] || feature;
    const featureIcon = (feature) => FEATURE_ICONS[feature] || 'fas fa-microchip';
    const featureColor = (feature) => FEATURE_COLORS[feature] || 'secondary';

    // --- Estado derivado -------------------------------------------------
    const periodo = computed(() => store.state.appAiUsage.periodo);
    const totalUsd = computed(() => store.state.appAiUsage.totalUsd);
    const costoPorConversacionUsd = computed(() => store.state.appAiUsage.costoPorConversacionUsd);
    const porFuncion = computed(() => store.state.appAiUsage.porFuncion);
    const usuarios = computed(() => store.state.appAiUsage.usuarios);
    const miembros = computed(() => store.state.appAiUsage.miembros);
    const miembrosTotal = computed(() => store.state.appAiUsage.miembrosTotal);
    const loadingMiembros = computed(() => store.state.appAiUsage.loadingMiembros);
    // El endpoint solo devuelve la primera página (8), avisamos si hay más
    const miembrosTruncados = computed(
        () => miembrosTotal.value > miembros.value.length
    );

    const totalLlamadas = computed(() =>
        (usuarios.value || []).reduce((sum, u) => sum + Number(u.llamadas || 0), 0)
    );

    const totalConversaciones = computed(() =>
        (usuarios.value || []).reduce((sum, u) => sum + Number(u.conversaciones || 0), 0)
    );

    // por_funcion ordenado por costo y con su porcentaje sobre el total
    const funcionesOrdenadas = computed(() => {
        const lista = [...(porFuncion.value || [])].sort(
            (a, b) => Number(b.costo_usd || 0) - Number(a.costo_usd || 0)
        );
        const total = lista.reduce((sum, f) => sum + Number(f.costo_usd || 0), 0);
        return lista.map((f) => ({
            ...f,
            porcentaje: total > 0 ? (Number(f.costo_usd || 0) / total) * 100 : 0,
        }));
    });

    // La API ya devuelve los usuarios de mayor a menor gasto, pero lo reforzamos
    const usuariosOrdenados = computed(() =>
        [...(usuarios.value || [])].sort(
            (a, b) => Number(b.costo_usd || 0) - Number(a.costo_usd || 0)
        )
    );

    const sinConsumo = computed(
        () => !spinner.value && Number(totalUsd.value) === 0 && (usuarios.value || []).length === 0
    );

    const porcentajeDeTotal = (costo) => {
        if (!totalUsd.value) return 0;
        return (Number(costo || 0) / Number(totalUsd.value)) * 100;
    };

    return {
        spinner,
        alertTable,
        filtros,
        trm,
        setTrm,
        periodo,
        totalUsd,
        costoPorConversacionUsd,
        porFuncion,
        usuarios,
        miembros,
        miembrosTotal,
        miembrosTruncados,
        loadingMiembros,
        totalLlamadas,
        totalConversaciones,
        funcionesOrdenadas,
        usuariosOrdenados,
        sinConsumo,
        getAiUsage,
        buscarMiembros,
        aplicarFiltros,
        limpiarFiltros,
        aplicarRango,
        showAlert,
        formatUsd,
        formatCop,
        formatNumber,
        formatMs,
        formatFecha,
        featureLabel,
        featureIcon,
        featureColor,
        porcentajeDeTotal,
    };
}
